import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

/**
 * API configuration constants for Feedback API
 */
const API_CONFIG = {
  BASE_URL: '/feedback',
  TIMEOUT: 10 * 1000, // 10 seconds
  ERROR_MESSAGES: {
    FETCH_QUESTIONS_ERROR: 'Unable to fetch feedback questions.',
    FETCH_SCORES_ERROR: 'Unable to fetch feedback scores.',
    SUBMIT_ERROR: 'Unable to submit feedback.',
    GENERIC_ERROR: 'Unable to process feedback request.',
    NETWORK_ERROR: 'Network error occurred. Please check your connection.',
  },
} as const;

/**
 * Generic object type for API responses
 */
interface ApiResponse<T = unknown> {
  [key: string]: T;
}

/**
 * Custom error class for API-related errors
 */
class ApiError extends Error {
  public readonly statusCode?: number;

  public readonly originalError?: unknown;

  constructor(message: string, statusCode?: number, originalError?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.originalError = originalError;
  }
}

/**
 * Creates and returns a configured Axios instance for feedback API
 * @returns Configured Axios instance
 */
function getFeedbackApiInstance(): AxiosInstance {
  const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-User-Id': window?.context?.id || 0,
      'X-Group-Id': window?.context?.group?.id || 0,
      'X-Company-Id': window?.context?.company?.id || 0,
    };

  if (window?.context?.group?.acting_role_id) {
    headers['X-Role-Id'] = window?.context?.group?.acting_role_id;
  }
  return axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    timeoutErrorMessage: API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR,
    headers: headers,
  });
}

/**
 * Handles and transforms various error types into ApiError instances
 * @param error - The caught error
 * @returns Formatted ApiError instance
 */
function handleApiError(error: any): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    if (error?.response?.data) {
      const data = error.response.data as any;
      if (data.errors && Array.isArray(data.errors)) {
        const message = data.errors
          .map((e: any) => (typeof e === 'string' ? e : e.message || JSON.stringify(e)))
          .join(', ');
        return new ApiError(message, error.response.status, error);
      }
    }

    // Handle network errors (timeout, connection issues)
    if (!error.response) {
      return new ApiError(API_CONFIG.ERROR_MESSAGES.NETWORK_ERROR, 0, error);
    }

    const method = error.config?.method?.toUpperCase();
    let errorMessage: string = API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR;
    if (method === 'POST') {
      errorMessage = API_CONFIG.ERROR_MESSAGES.SUBMIT_ERROR;
    } else if (method === 'GET') {
      if (error.config?.url?.includes('question')) {
        errorMessage = API_CONFIG.ERROR_MESSAGES.FETCH_QUESTIONS_ERROR;
      } else if (error.config?.url?.includes('feedback/workorder')) {
        errorMessage = API_CONFIG.ERROR_MESSAGES.FETCH_SCORES_ERROR;
      }
    }

    return new ApiError(errorMessage, error.response.status, error);
  }

  return new ApiError(API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR, 0, error);
}

/**
 * Makes an API request with proper error handling and type safety
 * @param options - Axios request configuration
 * @returns Promise resolving to the API response data
 * @throws ApiError when the request fails
 */
export default async function makeFeedbackApiRequest<T = ApiResponse>(
  options: AxiosRequestConfig,
): Promise<T> {
  try {
    const apiInstance = getFeedbackApiInstance();
    const response = await apiInstance.request<T>(options);

    if (!response.data) {
      throw new ApiError(
        API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR,
        response.status,
        response,
      );
    }

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
